"use client";

import { c as _c } from "react/compiler-runtime";
import { useEffect, useState } from "react";
import { AnimatePresence, animate, motion, useMotionValue, useReducedMotion, useTransform } from "framer-motion";
import { Gauge } from "lucide-react";
import { CAPABILITY_PANEL } from "@/lib/utils/capabilityWeb.utils";

// The Core Web Vitals, in plain English — most visitors have never heard of LCP,
// so each metric carries a one-line description of what it actually measures. The
// card cycles through them one at a time, the gauge sweeping up to that metric's
// score like a PageSpeed / Lighthouse dial.
const VITALS = [{
  abbr: "FCP",
  name: "First Contentful Paint",
  desc: "How fast the first thing shows up on screen.",
  score: 99
}, {
  abbr: "LCP",
  name: "Largest Contentful Paint",
  desc: "How fast the main content finishes loading.",
  score: 98
}, {
  abbr: "CLS",
  name: "Cumulative Layout Shift",
  desc: "How steady the page stays as it loads.",
  score: 100
}, {
  abbr: "INP",
  name: "Interaction to Next Paint",
  desc: "How quickly it reacts when you tap or click.",
  score: 97
}, {
  abbr: "TTFB",
  name: "Time to First Byte",
  desc: "How fast the server starts answering.",
  score: 96
}];
const CYCLE_MS = 2800;
// Gauge ring geometry — circumference of the r=52 circle drives the dash sweep.
const RADIUS = 52;
const CIRC = 2 * Math.PI * RADIUS;
const CapabilitySpeedChart = () => {
  const $ = _c(38);
  const reduce = useReducedMotion();
  const [i, setI] = useState(0);
  const vital = VITALS[i];
  let t0;
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = () => {
      const t = setInterval(() => setI(_temp), CYCLE_MS);
      return () => clearInterval(t);
    };
    t1 = [];
    $[0] = t0;
    $[1] = t1;
  } else {
    t0 = $[0];
    t1 = $[1];
  }
  useEffect(t0, t1);
  const count = useMotionValue(0);
  const rounded = useTransform(count, _temp2);
  const dashOffset = useTransform(count, _temp3);
  let t2;
  if ($[2] !== count || $[3] !== reduce || $[4] !== vital.score) {
    t2 = () => {
      if (reduce) {
        count.set(vital.score);
        return;
      }
      count.set(0);
      const controls = animate(count, vital.score, {
        duration: 1.4,
        ease: "easeOut"
      });
      return () => controls.stop();
    };
    $[2] = count;
    $[3] = reduce;
    $[4] = vital.score;
    $[5] = t2;
  } else {
    t2 = $[5];
  }
  let t3;
  if ($[6] !== count || $[7] !== i || $[8] !== reduce || $[9] !== vital.score) {
    t3 = [i, vital.score, reduce, count];
    $[6] = count;
    $[7] = i;
    $[8] = reduce;
    $[9] = vital.score;
    $[10] = t3;
  } else {
    t3 = $[10];
  }
  useEffect(t2, t3);
  let t4;
  if ($[11] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = <div className="mb-4 flex items-center gap-1.5 text-[10px] font-semibold uppercase tracking-wide text-neutral-500"><Gauge size={13} /> Core Web Vitals</div>;
    $[11] = t4;
  } else {
    t4 = $[11];
  }
  let t5;
  let t6;
  let t7;
  let t8;
  if ($[12] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = {
      opacity: 0,
      y: 8
    };
    t6 = {
      opacity: 1,
      y: 0
    };
    t7 = {
      opacity: 0,
      y: -8
    };
    t8 = {
      duration: 0.4
    };
    $[12] = t5;
    $[13] = t6;
    $[14] = t7;
    $[15] = t8;
  } else {
    t5 = $[12];
    t6 = $[13];
    t7 = $[14];
    t8 = $[15];
  }
  let t9;
  if ($[16] !== vital.abbr) {
    t9 = <p className="text-lg font-black uppercase tracking-tight text-neutral-900">{vital.abbr}</p>;
    $[16] = vital.abbr;
    $[17] = t9;
  } else {
    t9 = $[17];
  }
  let t10;
  if ($[18] !== vital.name) {
    t10 = <p className="text-[10px] font-semibold uppercase tracking-wide text-neutral-400">{vital.name}</p>;
    $[18] = vital.name;
    $[19] = t10;
  } else {
    t10 = $[19];
  }
  let t11;
  if ($[20] !== vital.desc) {
    t11 = <p className="mt-1.5 text-xs leading-relaxed text-neutral-500">{vital.desc}</p>;
    $[20] = vital.desc;
    $[21] = t11;
  } else {
    t11 = $[21];
  }
  let t12;
  if ($[22] !== i || $[23] !== t10 || $[24] !== t11 || $[25] !== t9) {
    t12 = <div className="min-h-24 flex-1"><AnimatePresence mode="wait"><motion.div key={i} initial={t5} animate={t6} exit={t7} transition={t8}>{t9}{t10}{t11}</motion.div></AnimatePresence></div>;
    $[22] = i;
    $[23] = t10;
    $[24] = t11;
    $[25] = t9;
    $[26] = t12;
  } else {
    t12 = $[26];
  }
  let t13;
  if ($[27] === Symbol.for("react.memo_cache_sentinel")) {
    t13 = <circle cx="60" cy="60" r={RADIUS} fill="none" stroke="#e5e5e5" strokeWidth="10" />;
    $[27] = t13;
  } else {
    t13 = $[27];
  }
  let t14;
  if ($[28] !== dashOffset) {
    t14 = <svg width="96" height="96" viewBox="0 0 120 120">{t13}<g transform="rotate(-90 60 60)"><motion.circle cx="60" cy="60" r={RADIUS} fill="none" stroke="#212129" strokeWidth="10" strokeLinecap="round" strokeDasharray={CIRC} style={{
          strokeDashoffset: dashOffset
        }} /></g></svg>;
    $[28] = dashOffset;
    $[29] = t14;
  } else {
    t14 = $[29];
  }
  let t15;
  if ($[30] !== rounded) {
    t15 = <motion.span className="absolute inset-0 grid place-items-center text-2xl font-black text-card-foreground">{rounded}</motion.span>;
    $[30] = rounded;
    $[31] = t15;
  } else {
    t15 = $[31];
  }
  let t16;
  if ($[32] !== t14 || $[33] !== t15) {
    t16 = <div className="relative grid size-24 shrink-0 place-items-center">{t14}{t15}</div>;
    $[32] = t14;
    $[33] = t15;
    $[34] = t16;
  } else {
    t16 = $[34];
  }
  let t17;
  if ($[35] !== t12 || $[36] !== t16) {
    t17 = <div className={`${CAPABILITY_PANEL} p-4`}>{t4}<div className="flex items-center gap-4 pr-2">{t12}{t16}</div></div>;
    $[35] = t12;
    $[36] = t16;
    $[37] = t17;
  } else {
    t17 = $[37];
  }
  return t17;
};
export default CapabilitySpeedChart;
function _temp(v) {
  return (v + 1) % VITALS.length;
}
function _temp2(v_0) {
  return Math.round(v_0);
}
function _temp3(v_1) {
  return CIRC * (1 - v_1 / 100);
}